// ══ FU-06A (Idan) — THE PICKER AS A WIZARD, AS PURE FUNCTIONS ═══════════════════════════════════════════
// «הכי פשוט ללקוח, שלא יסתבך». SmartSendPicker asks ONE question at a time:
//   ① מאיזה מקור שולחים?  ② איך שולחים? (E · W · E+W)  ③ לאיזה קמפיין אימייל?  ④ איזו תבנית וואטסאפ?
//   ⑤ בדיקה לפני המיפוי — and «המשך למיפוי השדות» there is today's button, today's handler, today's gate.
// Every decision of the wizard is made here; the picker only draws it and dispatches what this file returns.
// No React, no redux, no component (I6): facts in, a view (or a list of guarded channel actions) out.
//
// THE CHANNEL CARDS MOVE THE SAME TWO REDUX FLAGS THE OLD CHANNEL ROW MOVED, THROUGH THE SAME TWO GUARDS:
//   · `withWhatsApp` — only as WaAddonCard's tile could (its `interactive`, WaAddonCard.tsx:173-179);
//   · `suppressEmail` — only as the picker's e-mail switch could (`emailToggle`, SmartSendPicker).
// `channelPath` finds the shortest sequence of those guarded clicks that reaches a card's channel set, and the
// picker dispatches exactly that sequence. A card no such sequence reaches is dimmed ('locked' / 'unavailable').
// One more write, and it is the entry reset that already exists (QA BUG-31, `resetWaForPicker`): a WhatsApp intent
// kept by the change-source trip is dropped while the account's WhatsApp is not Ready — the tile's own guard would
// otherwise freeze it on and leave no card possible (`wizardReconcile`).
//
// The invariants (pickerWizard.test.js runs them over the whole state space, and runs the old screen beside it):
//   S   «המשך למיפוי השדות» enabled ⇒ today's `continueBlocked` is false (`finalEnabled` contains it).
//   C   every configuration today's screen can send is reachable here, except three classes refused on purpose
//       (the test's `refusedClass`): e-mail on a source with no e-mail column (only where the account has
//       WhatsApp — step ② is where it is refused), WhatsApp while the account's Availability is not Ready, and no
//       sender number chosen among two or more. (A template the grid would not tick — not in the loaded list — is
//       refused too, by construction of `valid.whatsapp`; the S/C test does not explore a failing list.)
//   DIM a card is dimmed ⇔ today's guards cannot reach that channel set FROM HERE ∨ today's D4 clause blocks it ∨
//       one of the two added refusals (no e-mail column; WhatsApp not Ready). This is NOT "⇔ continueBlocked would
//       block": continueBlocked has no clause for the two added refusals nor for reachability.
//   I1  a step shows ✓ only while its choice is valid now (re-derived on every call).
//   I2  the review step shows every warning SourcePicker showed about the chosen source (its identity banners and
//       the selected card's "no recipients" caption) and the seed refusal — "no recipients" for every lit channel.
//       The tile's two lines (its blocked reason, WA_NO_CELL_IDENTITY) are the dimmed cards' reasons in step ②.
//   I4  the channel set is never empty — the cards are {E, W, EW}, and the guards refuse the click that would
//       empty it (the tile's lastChannel).
//   I5  the active step is always on the bar and never locked; «המשך»/«חזרה»/the bar only go to such steps;
//       back always, forward only over answered steps.
//   I6  pure (this file), and dispatch only of guarded actions (`channelPath`) or the existing entry reset.
//   I7  R2 (?campaignId with a mapping) redirects before any of this is drawn — the picker's lines are untouched.
//   I8  (QA BUG-35) the channels a Continue SENDS — the units in the row of the page it opens — are the channels chosen
//       in ②. ② changes only by the operator's explicit choice («השאר אימייל + וואטסאפ»). See `continueDecision`.

import { continueBlockedOf, ContinueGateInput, Missing } from './pickerSections';

export type ChannelSet = 'E' | 'W' | 'EW';
export const CHANNEL_SETS: ChannelSet[] = ['E', 'W', 'EW'];
export type WizardStep = 'source' | 'channels' | 'email' | 'whatsapp' | 'review';
export const WIZARD_STEPS: WizardStep[] = ['source', 'channels', 'email', 'whatsapp', 'review'];

export interface Channels { email: boolean; whatsapp: boolean }
export const setOf = (ch: Channels): ChannelSet | null => {
    if (ch.email && ch.whatsapp) return 'EW';
    if (ch.whatsapp) return 'W';
    return ch.email ? 'E' : null;
};
export const channelsOfSet = (c: ChannelSet): Channels => ({ email: c !== 'W', whatsapp: c !== 'E' });

// ── the redux intent the cards move, and the facts the two guards read ──────────────────────────────────
export interface Intent { withWhatsApp: boolean; suppressEmail: boolean }
export interface GuardFacts {
    waAccount: boolean;         // selectWaAllowed ∧ selectWaEnabled — WhatsApp exists on this screen (WaAddonCard :141)
    waReady: boolean;           // availability.Ready (WaAddonCard :143)
    emailAlreadySent: boolean;  // the picker's `emailAlreadySent` (the selected campaign's leg, its mail out)
    noCell: boolean;            // the picker's `sourceNoCell` (D4: HasCellIdentity === false) — WaAddonCard's `noCell`
    hasLeg: boolean;            // smartSendWa.waMapping.HasLeg — the fallback arm of selectWithWhatsApp
}

// selectWaActive / selectEmailOnIntent as they answer on the picker: no unit (setWaUnit(null) on arrival) and no
// server channel array (nothing fetches it any more), so `withWhatsApp || waMapping.HasLeg` and the e-mail switch.
// ASSUMES `getChannels` is never dispatched on the picker (smartSendWa.channels stays null there).
// The test holds this to the real selectors over that state space.
export const channelsOf = (i: Intent, f: GuardFacts): Channels => {
    const whatsapp = f.waAccount && (i.withWhatsApp || f.hasLeg);
    return { email: !(whatsapp && i.suppressEmail), whatsapp };
};

export type ChannelAction = { kind: 'tile'; next: boolean } | { kind: 'email'; next: boolean };

// WaAddonCard `interactive` (:173-179) as the picker rendered it: no `onToggle`, `canToggle` at its default,
// `lastChannel={activeKeys.length === 1}`, `noCell={sourceNoCell}`, `leg={legForSelection}`.
const tileLive = (i: Intent, f: GuardFacts): boolean => {
    const ch = channelsOf(i, f);
    const lastChannel = (ch.email ? 1 : 0) + (ch.whatsapp ? 1 : 0) === 1;
    const checked = i.withWhatsApp;
    return f.waAccount && f.waReady && !f.emailAlreadySent && !(lastChannel && checked) && !(f.noCell && !checked);
};
// The picker's `emailToggle`: offered only while the tile is ticked, live unless the campaign's mail went out.
const emailSwitchLive = (i: Intent, f: GuardFacts): boolean => f.waAccount && i.withWhatsApp && !f.emailAlreadySent;

export const actionAllowed = (i: Intent, a: ChannelAction, f: GuardFacts): boolean => (a.kind === 'tile'
    ? a.next === !i.withWhatsApp && tileLive(i, f)
    : a.next === i.suppressEmail && emailSwitchLive(i, f));

// The two reducers (smartSendWhatsAppSlice): `setWithWhatsApp(false)` also restores the e-mail (:728-745);
// `emailToggle.onToggle(next)` is `setSuppressEmail(!next)` (:827). The test runs the real reducer beside this.
export const applyAction = (i: Intent, a: ChannelAction): Intent => (a.kind === 'tile'
    ? { withWhatsApp: a.next, suppressEmail: a.next ? i.suppressEmail : false }
    : { withWhatsApp: i.withWhatsApp, suppressEmail: !a.next });

// The shortest sequence of guarded clicks from `from` to an intent whose channels are `target`; null if none.
// Breadth-first over the four intents; each step is one click the old screen would have accepted.
export const channelPath = (from: Intent, target: ChannelSet, f: GuardFacts): ChannelAction[] | null => {
    const key = (i: Intent) => `${i.withWhatsApp ? 1 : 0}${i.suppressEmail ? 1 : 0}`;
    const seen = new Set<string>([key(from)]);
    let frontier: { i: Intent; path: ChannelAction[] }[] = [{ i: from, path: [] }];
    while (frontier.length) {
        const next: { i: Intent; path: ChannelAction[] }[] = [];
        for (let n = 0; n < frontier.length; n += 1) {
            const { i, path } = frontier[n];
            if (setOf(channelsOf(i, f)) === target) return path;
            const acts: ChannelAction[] = [{ kind: 'tile', next: !i.withWhatsApp }, { kind: 'email', next: i.suppressEmail }];
            acts.forEach((a) => {
                if (!actionAllowed(i, a, f)) return;
                const j = applyAction(i, a);
                if (seen.has(key(j))) return;
                seen.add(key(j));
                next.push({ i: j, path: [...path, a] });
            });
        }
        frontier = next;
    }
    return null;
};

// ── step ② · the three cards ────────────────────────────────────────────────────────────────────────────
// Why a card is dimmed, first match wins:
//   waOff   WhatsApp in it, and WhatsApp is not usable in this account (Availability not Ready)
//   noCell  WhatsApp in it, and the source has no mobile column — exactly today's D4 clause of `continueBlocked`
//   noEmail e-mail in it, and the source has no e-mail column (Idan: «המקור קובע»; today this was a banner)
//   locked       no sequence of the old screen's guarded clicks reaches it, because the selected campaign's mail
//                already went out (the tile and the e-mail switch are both frozen then)
//   unavailable  no such sequence, for any other reason (a WhatsApp leg left in the slice; never on the picker)
export type ChannelReason = 'waOff' | 'noCell' | 'noEmail' | 'locked' | 'unavailable';
export interface SourceIdentity { known: boolean; hasEmail: boolean }
export interface ChannelOption { set: ChannelSet; reason: ChannelReason | null; path: ChannelAction[] | null }

export const channelOptions = (i: Intent, f: GuardFacts, src: SourceIdentity): ChannelOption[] => CHANNEL_SETS.map((set) => {
    const ch = channelsOfSet(set);
    const path = channelPath(i, set, f);
    let reason: ChannelReason | null = null;
    if (ch.whatsapp && !(f.waAccount && f.waReady)) reason = 'waOff';
    else if (ch.whatsapp && f.noCell) reason = 'noCell';
    else if (ch.email && src.known && !src.hasEmail) reason = 'noEmail';
    else if (path == null) reason = f.emailAlreadySent ? 'locked' : 'unavailable';
    return { set, reason, path: reason ? null : path };
});

// The source grid of step ① is gated on what the ACCOUNT can send, not on a channel mix not chosen yet: a source
// is pickable when, from a fresh intent, at least one card of step ② could be lit for it (SourcePicker's OR, over
// these channels). A kept intent the account cannot honour is dropped first (`wizardReconcile`, `resetWa`).
export const sourceGateChannels = (f: GuardFacts): Channels => ({ email: true, whatsapp: f.waAccount && f.waReady });

// ── the view ─────────────────────────────────────────────────────────────────────────────────────────────
export interface WizardInput {
    fromUrl: boolean;                // ?dataSourceId= — the source is fixed (Idan R1): step ① is ✓ and locked
    intent: Intent;
    guard: GuardFacts;
    src: SourceIdentity;             // the selected source's row (the URL's, else the picked one), when loaded
    channels: Channels;              // what the store says now (selectEmailOnIntent / selectWaActive)
    chanChosen: boolean;             // step ② answered (by a click, by being the only option, by R2/the trip)
    valid: { source: boolean; email: boolean; whatsapp: boolean };   // each grid's own ✓ (see the picker)
    active: WizardStep | null;       // the step the operator is on; null before the first render settles it
    gate: ContinueGateInput;         // today's `continueBlocked` inputs, unchanged
    waUnknown?: boolean;             // WhatsApp's own answer (Availability) not in yet — the bar does not guess
}

export interface StepView {
    key: WizardStep;
    onBar: boolean;      // shown on the step bar (only the steps this send has)
    locked: boolean;     // ✓ and not clickable (the URL's source)
    done: boolean;       // ✓ — its choice is valid now (I1)
    auto: boolean;       // decided by the source/account (only one card possible): «המשך»/«חזרה» pass over it
    navigable: boolean;  // the bar may jump to it now
}
export type AutoNote = 'onlyEmailNoCell' | 'onlyEmailWaOff' | 'onlyWaNoEmail' | 'locked' | 'none';

// r2 (Idan: «לא נעים שהמספרים למעלה קופצים בכל שלב») — THE STEP BAR DRAWS ALL FIVE STEPS, ALWAYS WITH THE SAME
// NUMBERS: ① מקור ② איך שולחים ③ קמפיין אימייל ④ תבנית וואטסאפ ⑤ בדיקה. A step this send does not have is drawn grey,
// «לא נדרש», with the reason, and is never clickable; navigation (`bar`, «המשך»/«חזרה») is unchanged and skips it.
// A step whose presence is not decided yet (② not answered, or WhatsApp's own answer not in) is 'pending' («לפי שלב ②»),
// never «לא נדרש». The reason of a «לא נדרש» names what decided it: no WhatsApp in the account, the operator's own
// choice in ②, or — when ② was automatic — the dimmed card's own reason (the source's columns, WhatsApp not Ready…).
export const stepNumber = (k: WizardStep): number => WIZARD_STEPS.indexOf(k) + 1;
export type StepDisplay = 'active' | 'done' | 'open' | 'pending' | 'notNeeded';
export type NotNeededReason = 'noWaAccount' | 'noEmailChosen' | 'noWaChosen'
    | 'sourceNoEmail' | 'sourceNoCell' | 'waNotReady' | 'locked' | 'unavailable';
// r4 — the second line under a step's label, ALWAYS reserved (the picker draws it even when this is null), so no
// state changes the bar's geometry: «לא נדרש», «לפי שלב ②», or an automatic ②'s choice.
export type BarSub = 'notNeeded' | 'pending' | 'choice' | null;
export interface BarItem {
    key: WizardStep;
    number: number;
    display: StepDisplay;
    reason: NotNeededReason | null;   // why «לא נדרש»
    sub: BarSub;
    navigable: boolean;
    locked: boolean;
}
export interface WizardView {
    bar: WizardStep[];                 // the steps this send has, in order (what navigation walks)
    items: BarItem[];                  // what the step bar draws: always the five steps, numbered 1..5
    steps: Record<WizardStep, StepView>;
    active: WizardStep;
    options: ChannelOption[];
    chosenSet: ChannelSet | null;    // the card shown selected
    autoSet: ChannelSet | null;      // the one possible set, when only one is
    autoNote: AutoNote | null;       // why it is the only one («המקור קובע»)
    next: WizardStep | null;
    back: WizardStep | null;
    nextEnabled: boolean;
    finalEnabled: boolean;           // «המשך למיפוי השדות», before the picker's in-flight flags
}

// Why only one card is possible — or none. The reason of the card that is NOT possible names it; 'locked' only when
// that reason is the sent mail; no note for a cause the operator cannot read anything into ('unavailable').
const autoNoteOf = (options: ChannelOption[], autoSet: ChannelSet | null): AutoNote | null => {
    if (!options.length) return null;
    if (options.every((o) => o.reason != null)) return 'none';
    if (autoSet == null) return null;
    const why = (s: ChannelSet) => options.find((o) => o.set === s)?.reason ?? null;
    let r: ChannelReason | null = null;
    if (autoSet === 'W') r = why('E');
    else if (autoSet === 'E') r = why('W');
    else r = why('E') === 'locked' && why('W') === 'locked' ? 'locked' : null;
    if (r === 'noEmail') return 'onlyWaNoEmail';
    if (r === 'waOff') return 'onlyEmailWaOff';
    if (r === 'noCell') return 'onlyEmailNoCell';
    return r === 'locked' ? 'locked' : null;
};

export const wizardView = (w: WizardInput): WizardView => {
    const options = w.guard.waAccount ? channelOptions(w.intent, w.guard, w.src) : [];
    const ok = options.filter((o) => o.reason == null);
    const autoSet = ok.length === 1 ? ok[0].set : null;
    const cur = setOf(w.channels);
    const curOk = cur != null && ok.some((o) => o.set === cur);
    const chanDone = !w.guard.waAccount || (w.chanChosen && curOk);
    // The bar describes the send the operator WILL have: when ② is automatic and the store is not there yet (it moves
    // when ① is left — `wizardReconcile`), ③/④ follow the automatic set, so «המשך», the bar's targets and the first
    // open step never aim at a step that disappears a moment later. (The ✓ of ③/④ still reads the grids, I1.)
    const barCh: Channels = autoSet != null && cur !== autoSet ? channelsOfSet(autoSet) : w.channels;

    const onBar: Record<WizardStep, boolean> = {
        source: true,
        channels: w.guard.waAccount,
        email: barCh.email,
        whatsapp: barCh.whatsapp,
        review: true,
    };
    const done: Record<WizardStep, boolean> = {
        source: w.fromUrl || w.valid.source,
        channels: chanDone,
        email: !barCh.email || (w.channels.email && w.valid.email),
        whatsapp: !barCh.whatsapp || (w.channels.whatsapp && w.valid.whatsapp),
        review: false,
    };
    const bar = WIZARD_STEPS.filter((k) => onBar[k]);
    const locked = (k: WizardStep) => k === 'source' && w.fromUrl;
    // ② is "auto" when only one card is possible: it is (or, the moment the operator leaves step ①, will be) chosen
    // by itself (`wizardReconcile`). «המשך»/«חזרה» pass over it, and it counts as answered for moving forward; its
    // ✓ (`done`) still waits until the store is there (I1).
    const auto = (k: WizardStep) => k === 'channels' && autoSet != null;
    const passOver = (k: WizardStep) => locked(k) || auto(k);
    const answered = (k: WizardStep) => done[k] || passOver(k);
    const pos = (k: WizardStep) => bar.indexOf(k);
    const allAnsweredBefore = (k: WizardStep) => bar.slice(0, pos(k)).every((j) => answered(j));

    // Where the operator stands: their step, while it is on the bar and not locked; otherwise the first step
    // still to answer (the picker pins it, so later answers never move the operator while their step stays on the bar).
    const firstOpen = bar.find((k) => !answered(k)) ?? 'review';
    const active: WizardStep = w.active != null && pos(w.active) > -1 && !locked(w.active) ? w.active : firstOpen;

    const after = bar.slice(pos(active) + 1).find((k) => !passOver(k)) ?? null;
    const before = bar.slice(0, pos(active)).reverse().find((k) => !passOver(k)) ?? null;
    const nextEnabled = after != null && done[active] && allAnsweredBefore(after);

    const steps = {} as Record<WizardStep, StepView>;
    WIZARD_STEPS.forEach((k) => {
        const on = pos(k) > -1;
        steps[k] = {
            key: k,
            onBar: on,
            locked: on && locked(k),
            done: on && k !== 'review' && done[k],
            auto: on && auto(k),
            // Back to any earlier step; forward only over answered steps.
            navigable: on && !locked(k) && k !== active && (pos(k) < pos(active) || allAnsweredBefore(k)),
        };
    });

    const finalEnabled = active === 'review'
        && bar.filter((k) => k !== 'review').every((k) => done[k])
        && !continueBlockedOf(w.gate);

    const cardReason = (set: ChannelSet): NotNeededReason => {
        const r = options.find((o) => o.set === set)?.reason ?? null;
        if (r === 'noEmail') return 'sourceNoEmail';
        if (r === 'noCell') return 'sourceNoCell';
        if (r === 'waOff') return 'waNotReady';
        return r === 'locked' ? 'locked' : 'unavailable';
    };
    const notNeededWhy = (k: WizardStep): NotNeededReason => {
        if (k === 'channels' || !w.guard.waAccount) return 'noWaAccount';
        // ② automatic: the card of that channel alone is dimmed — its reason is why.
        if (autoSet != null) return cardReason(k === 'email' ? 'E' : 'W');
        return k === 'email' ? 'noEmailChosen' : 'noWaChosen';
    };
    // Whether ③/④ (and, before WhatsApp answered, ②) are in this send is not known yet.
    const undecided = !!w.waUnknown || (w.guard.waAccount && autoSet == null && !chanDone);
    const dependsOnChannels = (k: WizardStep) => k === 'email' || k === 'whatsapp' || (k === 'channels' && !!w.waUnknown);
    // An automatic ② that is chosen shows its choice on the second line (whatever the step's display).
    const choiceSub = (k: WizardStep): BarSub => (k === 'channels' && steps.channels.auto && steps.channels.done ? 'choice' : null);
    const items: BarItem[] = WIZARD_STEPS.map((k) => {
        const s = steps[k];
        const n = stepNumber(k);
        if (k === active) return { key: k, number: n, display: 'active' as const, reason: null, sub: choiceSub(k), navigable: false, locked: false };
        if (undecided && dependsOnChannels(k)) {
            return {
                key: k, number: n, display: 'pending' as const, reason: null,
                sub: (k === 'channels' ? null : 'pending') as BarSub, navigable: false, locked: false,
            };
        }
        if (!s.onBar) {
            return { key: k, number: n, display: 'notNeeded' as const, reason: notNeededWhy(k), sub: 'notNeeded' as const, navigable: false, locked: false };
        }
        const display: StepDisplay = s.done || s.locked ? 'done' : 'open';
        return { key: k, number: n, display, reason: null, sub: choiceSub(k), navigable: s.navigable, locked: s.locked };
    });

    return {
        bar,
        items,
        steps,
        active,
        options,
        chosenSet: w.chanChosen && curOk ? cur : null,
        autoSet,
        autoNote: autoNoteOf(options, autoSet),
        next: after,
        back: before,
        nextEnabled,
        finalEnabled,
    };
};

// ── after each render: step ② keeps up with the source ───────────────────────────────────────────────────
// First: a WhatsApp intent the account cannot honour now (ticked, Availability not Ready — only the change-source
// trip keeps one) is dropped with the existing entry reset (`resetWa` ⇒ the picker dispatches `resetWaForPicker`).
// The tile's own guard cannot un-tick it while not Ready, so without this no card would be possible.
// Then: only one card possible ⇒ it is chosen (and the store moved to it through the guards). The chosen card
// dimmed by a new source ⇒ the question is open again. Nothing else changes the channels on its own.
export interface WizardFix { chanChosen: boolean; apply: ChannelAction[] | null; resetWa: boolean }
export const wizardReconcile = (w: WizardInput): WizardFix => {
    if (!w.guard.waAccount) return { chanChosen: w.chanChosen, apply: null, resetWa: false };
    if (!w.guard.waReady && w.intent.withWhatsApp) return { chanChosen: w.chanChosen, apply: null, resetWa: true };
    // While the operator is ON step ① a click on a source only browses: nothing moves until «המשך» leaves the step
    // (an e-mail-only source passed over must not switch WhatsApp off and drop the template). The URL-source entry
    // never stands on ①, so it settles at once.
    if (wizardView(w).active === 'source') return { chanChosen: w.chanChosen, apply: null, resetWa: false };
    const ok = channelOptions(w.intent, w.guard, w.src).filter((o) => o.reason == null);
    const cur = setOf(w.channels);
    if (ok.length === 1) return { chanChosen: true, apply: cur === ok[0].set ? null : ok[0].path, resetWa: false };
    if (w.chanChosen && !(cur != null && ok.some((o) => o.set === cur))) return { chanChosen: false, apply: null, resetWa: false };
    return { chanChosen: w.chanChosen, apply: null, resetWa: false };
};

// A step change puts different controls under the same pointer (React 17 delivers the second click of a double click
// to whatever is there now). A click that lands less than CLICK_GAP_MS after the step changed is dropped anywhere in
// the wizard (the picker's capture-phase guard, and again in «המשך» / «המשך למיפוי השדות»), so a double click moves
// exactly one step and selects nothing on the new one; the final button is moreover a separate button in ⑤'s card.
export const CLICK_GAP_MS = 400;
export const acceptsClick = (stepShownAt: number, now: number): boolean => now - stepShownAt >= CLICK_GAP_MS;

// Leaving ① settles step ② IN THE SAME HANDLER, before the step changes (r2): the store moves first, so the step the
// operator lands on — ③/④, mounted on the store's channels — is already there: no empty frame, the focus reaches its
// question, and the template list starts loading at once. Null when the move does not leave ①.
export const leaveFix = (w: WizardInput, target: WizardStep): WizardFix | null => {
    if (wizardView(w).active !== 'source' || target === 'source') return null;
    return wizardReconcile({ ...w, active: target });
};

// Where the focus goes when the step changes (r3). Moving it to the new step's question helps a keyboard or screen-
// reader user, but the site's accessibility overlay frames every focused element in yellow — after a MOUSE click
// that frame read as a bug. So: the question takes the focus only when the step change came from the keyboard; after
// a pointer the focus stays where it was (the bar is scrolled into view only if its top is off screen — r4), and is dropped quietly only when the
// control that had it is gone or disabled. Navigation itself is the same in all three cases.
export type ArrivalFocus = 'heading' | 'keep' | 'blur';
export const arrivalFocus = (byKeyboard: boolean, focusStillUsable: boolean): ArrivalFocus => {
    if (byKeyboard) return 'heading';
    return focusStillUsable ? 'keep' : 'blur';
};

// A click on a card: the guarded clicks that light it, or null when it is dimmed.
export const chooseChannels = (w: WizardInput, set: ChannelSet): ChannelAction[] | null => {
    const o = channelOptions(w.intent, w.guard, w.src).find((x) => x.set === set);
    return o && o.reason == null ? o.path : null;
};

// ── QA BUG-35 (Idan) · «המשך למיפוי השדות» AND THE SEND'S ROW (I8) ──────────────────────────────────────────
// The server sends what the e-mail campaign's ROW holds, not what ② chose: PUT Send reads the row's WhatsApp unit
// (RowWACampaignID) and sends it with the e-mail (DataSourcesSenderController.cs:896-901, :1266-1281). An earlier
// «אימייל + וואטסאפ» Continue joined a unit into that row (POST WhatsApp/Container with CampaignID →
// CampaignsToDataSources_Join), so a later «אימייל» Continue on the same campaign opened a "both" page and sent both.
// How each Continue keeps I8:
//   W   a new unit, or one reused from a row WITHOUT an e-mail (FindReusableWaUnit skips rows holding one, :1957):
//       its own row ⇒ sends W.
//   EW  the chosen unit is joined into the e-mail's row (Join replaces an editable unit already there, 8.2; a sent one
//       is refused, -41) ⇒ sends E+W.
//   E   the row is read first (the read the source check already made, now every time): no unit ⇒ go; a unit ⇒ the
//       operator is asked; a failed read ⇒ stay with a message (nothing is known about the row, so nothing is opened).
export interface SendRow { read: boolean; wa: boolean }   // the e-mail campaign's row, as the Continue's read answered
export type ContinueDecision = 'go' | 'askUnit' | 'readFailed';
export const continueDecision = (ch: Channels, row: SendRow | null): ContinueDecision => {
    if (!ch.email || ch.whatsapp) return 'go';
    if (row == null || !row.read) return 'readFailed';
    return row.wa ? 'askUnit' : 'go';
};

// The answers. «הסר וואטסאפ» = POST Leave (splits the row; deletes nothing — the unit keeps a row of its own), then the
// e-mail-only page, ONLY on 200. Decoded as the mapping page's `runLeave` decodes it (the code is in the body):
// SEND_ALREADY_SENT (-41, a channel already went out), NOT_A_BOTH_SEND (-45, the row no longer holds THAT unit — split
// meanwhile, or another unit joined), and any other failure stay, with the reason; a new Continue reads the row again.
export type LeaveOutcome = 'ok' | 'sent' | 'changed' | 'failed';
export const leaveOutcome = (statusCode: number | null | undefined, message: string | null | undefined): LeaveOutcome => {
    if (statusCode === 200) return 'ok';
    if (message === 'SEND_ALREADY_SENT') return 'sent';
    if (message === 'NOT_A_BOTH_SEND') return 'changed';
    return 'failed';
};
// «השאר אימייל + וואטסאפ» = ② becomes E+W through the old channel row's guards (exactly a click on that card); null ⇒
// not offered (the account's WhatsApp off or not Ready, a source with no mobile column, the mail already out).
export const keepBothPath = (w: WizardInput): ChannelAction[] | null => chooseChannels(w, 'EW');

// ── step ⑤ · every warning about what is about to be sent, in one place (I2) ──────────────────────────────
// Today's set is SourcePicker's identity banners for the selected source and its card's red "no recipients"
// caption (for the channel that sends), plus the seed refusal. Here: the same banners for any known source (not
// only one picked in the grid), and "no recipients" for EVERY lit channel — a superset, never less.
export type ReviewWarning = 'noEmail' | 'noEmailBoth' | 'noCell' | 'noCellBoth' | 'zeroEmail' | 'zeroWa' | 'seedRefused';
export interface ReviewFacts {
    channels: Channels;
    waActive: boolean;
    srcMissing: Missing;          // missingIdentity(row, channels), 'none' without a row
    legNoCell: boolean;
    emailCount: number | null;    // row[resolvedCountField], null without a row
    cellCount: number | null;     // row.ResolvedRowsCell, null without a row
    seedRefused: boolean;
}
export const reviewWarnings = (r: ReviewFacts): ReviewWarning[] => {
    const bothOn = r.channels.email && r.channels.whatsapp;
    const out: ReviewWarning[] = [];
    if (r.waActive && (r.srcMissing === 'email' || r.srcMissing === 'both')) out.push(bothOn ? 'noEmailBoth' : 'noEmail');
    if (r.waActive && (r.srcMissing === 'cell' || r.srcMissing === 'both') && !r.legNoCell) out.push(bothOn ? 'noCellBoth' : 'noCell');
    if (r.channels.email && r.emailCount === 0) out.push('zeroEmail');
    if (r.channels.whatsapp && r.cellCount === 0) out.push('zeroWa');
    if (r.channels.email && r.seedRefused) out.push('seedRefused');
    return out;
};
